import { useMutation, useQuery } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import Modal from '../../../../../../components/modals/Modal';
import { Button } from '../../../../../../components/common';
import { MultiSelectInput, RadioGroupInput } from '../../../../../../components/form';
import clsx from 'clsx';
import thirdPartyClientService from '../../../../../../services/api/payments/thirdPartyClientService';
import clientsService from '../../../../../../services/api/payments/clientsService';
import { capitalizeFirstLetter } from '../../../../../../utils/strings';
import useToast from '../../../../../../state/hooks/useToast';


function ThirdClientFormModal({ open, thirdParty, onClose, onAdded, onUpdated }: Props) {
    const { addToast } = useToast();
    const [searchQuery, setSearchQuery] = useState('');


    useEffect(() => {
        if (open) {
            saveMutation.reset();
        }
    }, [open]);


    const schoolsQueryKey = ['search', 'schools', { name: searchQuery }];
    const schoolsQuery = useQuery({
        queryKey: schoolsQueryKey,
        queryFn: () => clientsService.getClients({ name: searchQuery }),
        enabled: !!searchQuery,
        staleTime: Infinity,
        retryDelay: Infinity,
        refetchOnWindowFocus: false, // Disable refetching on window focus
        refetchOnMount: false, // Disable refetching on component mount
        refetchInterval: false, // Disable interval-based refetching
    });


    const saveMutation = useMutation({
        mutationFn: (data: any) => thirdPartyClientService.addRemoveClient(thirdParty?.id, data)
    });


    const handleFormSubmit = async (formData: any, { resetForm }: any) => {
        const payload = {
            ...formData,
            clientIds: formData.clientIds?.map((item: any) => item.id)
        };
        saveMutation.mutate(payload, {
            onSuccess: (data) => {
                addToast({
                    text: `School(s) ${formData.action === 'ADD' ? 'Added' : 'Removed'} successfully`,
                    type: "success"
                });
                onClose();
            },
            onError: () => {
                addToast({
                    text: `Could not ${capitalizeFirstLetter(formData.action)}.`,
                    type: "error"
                });
            }
        });
    }


    return (
        <Modal open={open} onClose={false ? () => null : onClose} closeOnOutsideClicked={false}>
            <div className="inline-block w-[36rem] max-w-full">
                <div className="flex justify-between mb-5 px-10 pt-8">
                    <h2 className="font-medium text-xl">
                        Add / Remove Clients
                    </h2>

                    <button type="button" onClick={onClose} className="text-2xl text-gray-500 [&:not(:disabled)]:hover:text-gray-600 dark:text-gray-600 dark:[&:not(:disabled)]:hover:text-gray-500 focus:outline-none" disabled={saveMutation.isPending}>
                        <i className="ri-close-line"></i>
                    </button>
                </div>


                <div className='max-h-[80vh] overflow-y-auto px-10 pb-6'>
                    <Formik
                        initialValues={{
                            action: '',
                            clientIds: [],
                        }}
                        validationSchema={validationSchema}
                        onSubmit={handleFormSubmit}
                    >
                        {({ values, errors, touched, setErrors, setFieldValue, setFieldTouched, submitCount, isValid }) => (
                            <Form className="">
                                {
                                    saveMutation.error &&
                                    <p className='text-red-500 mb-4'>{saveMutation.error as unknown as string}</p>
                                }


                                <div className='mb-8'>
                                    <div className="">
                                        <label htmlFor="modal-radio-action" className='block text-sm mb-2'>Select Action</label>
                                        <RadioGroupInput
                                            value={values["action"]}
                                            options={[
                                                { label: "Add Clients", value: 'ADD' },
                                                { label: "Remove Clients", value: 'REMOVE' },
                                            ]}
                                            onChange={(value) => setFieldValue("action", value)}
                                        />
                                        {touched['action'] && errors['action'] && <p className='text-sm text-red-500 mt-2'>Action is required</p>}
                                    </div>

                                    {
                                        values.action &&
                                        <div className={clsx('rounded-xl font-medium mt-4 py-2 px-3', {
                                            'bg-green-500/5 text-green-500': values.action === 'ADD',
                                            'bg-yellow-500/5 text-yellow-600': values.action === 'REMOVE'
                                        })}>
                                            {values.action === 'ADD' && `Schools selected will be added to this third party (${thirdParty?.companyName})`}
                                            {values.action === 'REMOVE' && `Schools selected will be removed from this third party (${thirdParty?.companyName})`}
                                        </div>
                                    }
                                </div>


                                <div className='mb-10'>
                                    <label htmlFor="modal-select-groups" className='block mb-2'>Select schools</label>
                                    <div className="">
                                        <MultiSelectInput
                                            items={schoolsQuery.data?.data}
                                            valueKey='id'
                                            labelKey='name'
                                            placeholder="Select school"
                                            value={values["clientIds"]}
                                            itemsShowLimit={6}
                                            onChange={(value) => setFieldValue("clientIds", value)}
                                            onBlur={() => setFieldTouched('clientIds', true)}
                                            onSearch={(query) => setSearchQuery(query)}
                                            searching={schoolsQuery.isFetching}
                                            placement='bottom-end'
                                            usePortal
                                            wrapOptions
                                        />
                                        {touched['clientIds'] && errors['clientIds'] && <p className='text-sm text-red-500 mt-2'>Select a school or more to save</p>}
                                    </div>
                                </div>


                                <div className='flex justify-end items-center'>
                                    <Button type='button' onClick={onClose} className='!text-gray-500 dark:!text-white bg-gray-100 hocus:bg-gray-200  dark:bg-gray-800 dark:hocus:bg-gray-600 py-2.5 mr-6'>
                                        Cancel
                                    </Button>

                                    <Button type='submit' loading={saveMutation.isPending} className='' disabled={saveMutation.isPending}>
                                        {capitalizeFirstLetter(values.action) || 'Save'}
                                    </Button>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </div>
            </div>
        </Modal>
    );
}


const validationSchema = yup.object().shape({
    action: yup.string().trim().required(),
    clientIds: yup.array().min(1)
});


interface Props {
    open: boolean;
    thirdParty: any;
    onClose: () => void;
    onAdded?: (data: any) => void;
    onUpdated?: (data: any) => void;
}

const entityList = [
    { label: 'Customer', value: 'CUSTOMER' },
    { label: 'Invoice', value: 'INVOICE' },
    { label: 'Sika Id', value: 'SIKA_ID' },
    { label: 'Transaction', value: 'TRANSACTION' },
];
// CUSTOMER, INVOICE, SIKA_ID, TRANSACTION - entities

const eventList = [
    { label: 'Create', value: 'CREATE' },
    { label: 'Update', value: 'UPDATE' },
    //{ label: 'Delete', value: 'DELETE' },
]

export default ThirdClientFormModal;